import { useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { copyText } from '@/lib/copyText';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';

/** A plaintext secret the server returned once. Lives only in component state while its dialog is open. */
export interface RevealedSecret {
  /** Remount key: a new id resets the acknowledgement. */
  id: number | string;
  /** Dialog title, e.g. "API key created". */
  title: string;
  /** Dialog description, e.g. "Copy 'prod' now. It will not be shown again." */
  description: string;
  /** Label of the read-only input, e.g. "API key" / "Signing secret". */
  label: string;
  value: string;
  /** Body of the "Shown once." note. */
  warning: string;
  /** Text next to the checkbox that gates "Done". Defaults to the API key wording. */
  acknowledgement?: string;
}

const DEFAULT_ACKNOWLEDGEMENT = 'I have copied this key and stored it somewhere safe.';

interface SecretRevealDialogProps {
  reveal: RevealedSecret | null;
  onDone: () => void;
}

/**
 * The plaintext secret is shown exactly once. Every close path (Escape, outside click, X) is ignored; the only
 * exit is "Done", which is enabled after the user acknowledges they stored the secret. The body unmounts on
 * close, so the secret is not kept in state afterwards.
 */
export function SecretRevealDialog({ reveal, onDone }: SecretRevealDialogProps) {
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog open={reveal !== null} onOpenChange={() => undefined}>
      {reveal ? (
        <RevealBody key={reveal.id} reveal={reveal} onDone={onDone} captureOpener={captureOpener} restoreFocus={restoreFocus} />
      ) : null}
    </Dialog>
  );
}

function RevealBody({
  reveal,
  onDone,
  captureOpener,
  restoreFocus,
}: {
  reveal: RevealedSecret;
  onDone: () => void;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}) {
  const inputId = useId();
  const copyRef = useRef<HTMLButtonElement>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <DialogContent
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        copyRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
    >
      <DialogHeader>
        <DialogTitle>{reveal.title}</DialogTitle>
        <DialogDescription>{reveal.description}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div role="note" className="rounded-lg border border-status-warning-soft bg-status-warning-soft p-3 text-sm text-status-warning">
          <strong className="font-semibold">Shown once.</strong> {reveal.warning}
        </div>

        <div className="space-y-2">
          <label htmlFor={inputId} className="text-sm font-medium">
            {reveal.label}
          </label>
          <div className="flex gap-2">
            <Input id={inputId} readOnly value={reveal.value} onFocus={(e) => e.currentTarget.select()} className="font-mono text-sm" />
            <Button ref={copyRef} type="button" variant="outline" onClick={() => void copyText(reveal.value)}>
              Copy
            </Button>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <span>{reveal.acknowledgement ?? DEFAULT_ACKNOWLEDGEMENT}</span>
        </label>
      </div>

      <DialogFooter>
        <Button type="button" disabled={!acknowledged} onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
